/**
 * Dashboard notification preferences for newly curated trends.
 *
 * Stored client-side (per browser) for instant reads, and mirrored to the
 * signed-in user's profile row so the choice follows them across devices.
 */

export type TrendNotifyMode = "instant" | "daily" | "muted";

/** One past new-trend announcement, kept so it can be reviewed later. */
export type TrendNotifyHistoryEntry = {
  /** Stable id (also used as the React key and the dismiss/read target). */
  id: string;
  kind: "instant" | "summary";
  trendIds: string[];
  title: string;
  createdAt: number;
  read: boolean;
  dismissed: boolean;
};

export type TrendNotifySettings = {
  mode: TrendNotifyMode;
  /** Epoch ms of the last daily-summary toast that was shown. */
  lastSummaryAt: number | null;
  /** Trend ids that arrived while in daily mode and haven't been summarised. */
  pending: string[];
  /** Trend ids the user has already viewed or dismissed — never re-announced. */
  seenIds: string[];
  /** Announced trend ids the user hasn't read yet (drives the unread badge). */
  unreadIds: string[];
  /** Reviewable log of past announcements (newest first). */
  history: TrendNotifyHistoryEntry[];
  /** Also send new-trend alerts by email. */
  email: boolean;
  /** Also send new-trend alerts as browser/web push notifications. */
  push: boolean;
};

export const TREND_NOTIFY_KEY = "td:trend-notifications:v1";
export const DAILY_SUMMARY_INTERVAL_MS = 24 * 60 * 60 * 1000;
/** Keeps the seen list from growing without bound. */
export const MAX_SEEN_IDS = 500;
/** Keeps the reviewable history bounded. */
export const MAX_HISTORY_ENTRIES = 50;

export const DEFAULT_TREND_NOTIFY_SETTINGS: TrendNotifySettings = {
  mode: "instant",
  lastSummaryAt: null,
  pending: [],
  seenIds: [],
  unreadIds: [],
  history: [],
  email: false,
  push: false,
};

const strings = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];

const bool = (v: unknown, fallback: boolean): boolean =>
  typeof v === "boolean" ? v : fallback;

/** Normalises unknown input into history entries, newest first. */
export function parseHistory(v: unknown): TrendNotifyHistoryEntry[] {
  if (!Array.isArray(v)) return [];
  return v
    .filter((e): e is Record<string, unknown> => !!e && typeof e === "object")
    .map((e): TrendNotifyHistoryEntry => ({
      id: typeof e.id === "string" ? e.id : `n-${Math.random().toString(36).slice(2)}`,
      kind: e.kind === "summary" ? "summary" : "instant",

      trendIds: strings(e.trendIds),
      title: typeof e.title === "string" ? e.title : "New curated trend",
      createdAt: typeof e.createdAt === "number" ? e.createdAt : 0,
      read: bool(e.read, false),
      dismissed: bool(e.dismissed, false),
    }))
    .sort((a, b) => b.createdAt - a.createdAt)
    .slice(0, MAX_HISTORY_ENTRIES);
}

export function readTrendNotifySettings(): TrendNotifySettings {
  try {
    const raw = window.localStorage.getItem(TREND_NOTIFY_KEY);
    if (!raw) return { ...DEFAULT_TREND_NOTIFY_SETTINGS };
    const parsed = JSON.parse(raw) as Partial<TrendNotifySettings>;
    const mode: TrendNotifyMode =
      parsed?.mode === "daily" || parsed?.mode === "muted" ? parsed.mode : "instant";
    return {
      mode,
      lastSummaryAt: typeof parsed?.lastSummaryAt === "number" ? parsed.lastSummaryAt : null,
      pending: strings(parsed?.pending),
      seenIds: strings(parsed?.seenIds),
      unreadIds: strings(parsed?.unreadIds),
      history: parseHistory(parsed?.history),
      email: bool(parsed?.email, false),
      push: bool(parsed?.push, false),
    };
  } catch {
    return { ...DEFAULT_TREND_NOTIFY_SETTINGS };
  }
}


/* ------------------------------------------------------------------ *
 * Remote mirror (registered at app start so this module stays pure)
 * ------------------------------------------------------------------ */

type RemoteWriter = (settings: TrendNotifySettings) => void;
let remoteWriter: RemoteWriter | null = null;

/** Registers the profile-sync writer used to persist settings server-side. */
export function setTrendNotifyRemoteWriter(fn: RemoteWriter | null): void {
  remoteWriter = fn;
}

export function writeTrendNotifySettings(
  next: TrendNotifySettings,
  options: { remote?: boolean } = {},
): void {
  try {
    window.localStorage.setItem(TREND_NOTIFY_KEY, JSON.stringify(next));
  } catch {
    /* storage unavailable — preferences are best-effort */
  }
  if (options.remote !== false) {
    try {
      remoteWriter?.(next);
    } catch {
      /* remote mirror is best-effort */
    }
  }
  try {
    window.dispatchEvent(new CustomEvent("td:trend-notify-settings", { detail: next }));
  } catch {
    /* noop */
  }
}

/** Union of two settings objects: remote mode wins, id lists merge. */
export function mergeTrendNotifySettings(
  local: TrendNotifySettings,
  remote: Partial<TrendNotifySettings>,
): TrendNotifySettings {
  const mode =
    remote.mode === "instant" || remote.mode === "daily" || remote.mode === "muted"
      ? remote.mode
      : local.mode;
  const uniq = (a: string[], b: string[]) => Array.from(new Set([...a, ...b]));
  const seenIds = uniq(local.seenIds, strings(remote.seenIds)).slice(-MAX_SEEN_IDS);
  const seen = new Set(seenIds);
  return {
    mode,
    lastSummaryAt:
      typeof remote.lastSummaryAt === "number"
        ? Math.max(remote.lastSummaryAt, local.lastSummaryAt ?? 0)
        : local.lastSummaryAt,
    pending: uniq(local.pending, strings(remote.pending)).filter((id) => !seen.has(id)),
    seenIds,
    unreadIds: uniq(local.unreadIds, strings(remote.unreadIds)).filter((id) => !seen.has(id)),
    history: mergeHistory(local.history, parseHistory(remote.history)),
    email: typeof remote.email === "boolean" ? remote.email : local.email,
    push: typeof remote.push === "boolean" ? remote.push : local.push,
  };
}

/**
 * Merges two history logs by entry id. Read/dismissed are sticky: once either
 * device marks an entry read or dismissed, that wins — which is what makes the
 * optimistic local update safe to apply before the remote write lands.
 */
export function mergeHistory(
  a: TrendNotifyHistoryEntry[],
  b: TrendNotifyHistoryEntry[],
): TrendNotifyHistoryEntry[] {
  const byId = new Map<string, TrendNotifyHistoryEntry>();
  for (const entry of [...a, ...b]) {
    const prev = byId.get(entry.id);
    byId.set(
      entry.id,
      prev
        ? {
            ...prev,
            ...entry,
            read: prev.read || entry.read,
            dismissed: prev.dismissed || entry.dismissed,
          }
        : entry,
    );
  }
  return Array.from(byId.values())
    .sort((x, y) => y.createdAt - x.createdAt)
    .slice(0, MAX_HISTORY_ENTRIES);
}


export type ToastPlan =
  | { kind: "none" }
  | { kind: "instant"; ids: string[] }
  | { kind: "summary"; ids: string[] };

/**
 * Decides what (if anything) to announce for the newly arrived trend ids,
 * given the current preferences. Trends the user already viewed or dismissed
 * are never announced again. Returns the plan plus the settings to persist.
 */
export function planTrendToast(
  addedIds: string[],
  settings: TrendNotifySettings,
  now: number = Date.now(),
): { plan: ToastPlan; settings: TrendNotifySettings } {
  const seen = new Set(settings.seenIds);
  const fresh = addedIds.filter((id) => !seen.has(id));
  const withUnread = (ids: string[]): TrendNotifySettings => ({
    ...settings,
    unreadIds: Array.from(new Set([...settings.unreadIds, ...ids])),
  });

  if (settings.mode === "muted") {
    return { plan: { kind: "none" }, settings: withUnread(fresh) };
  }
  if (settings.mode === "instant") {
    if (fresh.length === 0) return { plan: { kind: "none" }, settings };
    return { plan: { kind: "instant", ids: fresh }, settings: withUnread(fresh) };
  }
  // daily
  const pending = Array.from(new Set([...settings.pending, ...fresh])).filter(
    (id) => !seen.has(id),
  );
  const queued = withUnread(fresh);
  if (pending.length === 0) return { plan: { kind: "none" }, settings: { ...queued, pending } };
  const due =
    settings.lastSummaryAt === null || now - settings.lastSummaryAt >= DAILY_SUMMARY_INTERVAL_MS;
  if (!due) return { plan: { kind: "none" }, settings: { ...queued, pending } };
  return {
    plan: { kind: "summary", ids: pending },
    settings: { ...queued, pending: [], lastSummaryAt: now },
  };
}

/** Marks a trend as viewed/dismissed so it never triggers a toast again. */
export function markTrendSeen(
  settings: TrendNotifySettings,
  trendId: string,
): TrendNotifySettings {
  return {
    ...settings,
    seenIds: Array.from(new Set([...settings.seenIds, trendId])).slice(-MAX_SEEN_IDS),
    pending: settings.pending.filter((id) => id !== trendId),
    unreadIds: settings.unreadIds.filter((id) => id !== trendId),
  };
}

/** Clears the unread badge; everything announced counts as seen. */
export function markAllTrendsRead(
  settings: TrendNotifySettings,
  now: number = Date.now(),
): TrendNotifySettings {
  return {
    ...settings,
    seenIds: Array.from(
      new Set([...settings.seenIds, ...settings.unreadIds, ...settings.pending]),
    ).slice(-MAX_SEEN_IDS),
    unreadIds: [],
    pending: [],
    history: settings.history.map((e) => (e.read ? e : { ...e, read: true })),
    lastSummaryAt: settings.mode === "daily" ? now : settings.lastSummaryAt,
  };
}

export const unreadTrendCount = (settings: TrendNotifySettings): number =>
  settings.unreadIds.length;

/* ------------------------------------------------------------------ *
 * Notification history
 * ------------------------------------------------------------------ */

/** Appends the announcement described by `plan` to the reviewable history. */
export function recordTrendNotification(
  settings: TrendNotifySettings,
  plan: ToastPlan,
  title: string,
  now: number = Date.now(),
): TrendNotifySettings {
  if (plan.kind === "none" || plan.ids.length === 0) return settings;
  const entry: TrendNotifyHistoryEntry = {
    id: `n-${now}-${plan.ids[0]}`,
    kind: plan.kind,
    trendIds: plan.ids,
    title,
    createdAt: now,
    read: false,
    dismissed: false,
  };
  return { ...settings, history: mergeHistory([entry], settings.history) };
}

/** Marks a single history entry read (does not touch trend seen state). */
export function markHistoryEntryRead(
  settings: TrendNotifySettings,
  entryId: string,
): TrendNotifySettings {
  return {
    ...settings,
    history: settings.history.map((e) => (e.id === entryId ? { ...e, read: true } : e)),
  };
}

/**
 * Dismisses a history entry: it stays in the log (greyed out) but its trends
 * count as seen so they are never announced again.
 */
export function dismissHistoryEntry(
  settings: TrendNotifySettings,
  entryId: string,
): TrendNotifySettings {
  const entry = settings.history.find((e) => e.id === entryId);
  const ids = entry?.trendIds ?? [];
  const seen = new Set([...settings.seenIds, ...ids]);
  return {
    ...settings,
    seenIds: Array.from(seen).slice(-MAX_SEEN_IDS),
    pending: settings.pending.filter((id) => !ids.includes(id)),
    unreadIds: settings.unreadIds.filter((id) => !ids.includes(id)),
    history: settings.history.map((e) =>
      e.id === entryId ? { ...e, read: true, dismissed: true } : e,
    ),
  };
}

/** Empties the reviewable history log (read state for trends is kept). */
export function clearTrendHistory(settings: TrendNotifySettings): TrendNotifySettings {
  return { ...settings, history: [] };
}

export const unreadHistoryCount = (settings: TrendNotifySettings): number =>
  settings.history.filter((e) => !e.read && !e.dismissed).length;

/* ------------------------------------------------------------------ *
 * Deep links
 * ------------------------------------------------------------------ */

/** Query param that deep-links straight to a trend details modal. */
export const TREND_DEEP_LINK_PARAM = "trend";

/** Dashboard URL that opens the details modal for `trendId` directly. */
export const trendDeepLinkPath = (trendId: string): string =>
  `/dashboard?${TREND_DEEP_LINK_PARAM}=${encodeURIComponent(trendId)}`;

/** Absolute deep link, used in email and push notification payloads. */
export function trendDeepLinkUrl(trendId: string, origin?: string): string {
  const base = origin ?? (typeof window !== "undefined" ? window.location.origin : "");
  return `${base}${trendDeepLinkPath(trendId)}`;
}


export const FOCUS_TREND_EVENT = "td:focus-trend";
export const OPEN_TREND_MODAL_EVENT = "td:open-trend-modal";

/**
 * Asks the dashboard to open the trend details modal (and the curated feed to
 * highlight the matching card behind it).
 */
export function requestFocusTrend(trendId: string): void {
  try {
    window.dispatchEvent(new CustomEvent(FOCUS_TREND_EVENT, { detail: { trendId } }));
    window.dispatchEvent(new CustomEvent(OPEN_TREND_MODAL_EVENT, { detail: { trendId } }));
  } catch {
    /* noop */
  }
}

/** Human-readable "how old is this data" label. */
export function formatDataAge(savedAt: number, now: number = Date.now()): string {
  const secs = Math.max(0, Math.round((now - savedAt) / 1000));
  if (secs < 45) return "just now";
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins} min${mins === 1 ? "" : "s"} ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}
